import { getUserFromLS } from "../utils/getUserFromLS";
import type {
  AccountQuery,
  AccountPage,
  AccountRankingScopePage,
  FacetField,
  FacetPage,
  MatchCandidate,
  MatchRow,
  ServerMatch,
  FrozenSelection,
  BulkAction,
  BulkPreview,
  BulkJob,
  OutcomePage,
} from "../Types/accountQuery";

export async function accountQueryRequest<T>(
  path: string,
  body?: unknown,
  signal?: AbortSignal,
  key?: string,
): Promise<T> {
  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}${path}`, {
    method: body === undefined ? "GET" : "POST",
    signal,
    headers: {
      Authorization: `Bearer ${getUserFromLS().access_token}`,
      "Content-Type": "application/json",
      ...(key ? { "Idempotency-Key": key } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    if (response.status === 410)
      throw new AccountQueryHttpError(
        "Selection expired. Select accounts again.", response.status,
      );
    if (response.status === 409)
      throw new AccountQueryHttpError(
        "Eligible accounts changed. Review and confirm again.", response.status,
      );
    if (response.status === 403)
      throw new AccountQueryHttpError(
        "You no longer have access to these accounts or this action.", response.status,
      );
    throw new AccountQueryHttpError("Could not load accounts. Please retry.", response.status);
  }
  return response.json();
}
export const queryAccounts = (q: AccountQuery, signal?: AbortSignal) =>
  accountQueryRequest<AccountPage>("/accounts/query", q, signal);
export const queryAccountRankingScope = (q: AccountQuery, signal?: AbortSignal) =>
  accountQueryRequest<AccountRankingScopePage>("/accounts/query/ranking-scope", q, signal);
export const queryFacet = (
  q: AccountQuery,
  field: FacetField,
  search = "",
  offset = 0,
  signal?: AbortSignal,
  selectedValues: string[] = [],
) =>
  accountQueryRequest<FacetPage>(
    "/accounts/query/facets",
    {
      ...q,
      field,
      option_search: search,
      limit: 50,
      offset,
      selected_values: selectedValues,
    },
    signal,
  );
export const querySuggestions = (q: AccountQuery, signal?: AbortSignal) =>
  accountQueryRequest<MatchCandidate[]>(
    "/accounts/query/suggestions",
    q,
    signal,
  );
export const queryMatches = (
  q: AccountQuery,
  rows: MatchRow[],
  signal?: AbortSignal,
  offset = 0,
) =>
  accountQueryRequest<ServerMatch[]>(
    "/accounts/query/match",
    { ...q, rows, candidate_offset: offset },
    signal,
  );
export const freezeAccounts = (q: AccountQuery, ids: string[] | null) =>
  accountQueryRequest<FrozenSelection>("/account-selections", {
    ...q,
    account_ids: ids,
  });
export const previewAccounts = (
  selection: string,
  action: BulkAction,
  signal?: AbortSignal,
) =>
  accountQueryRequest<BulkPreview>(
    `/account-selections/${selection}/preview`,
    { action },
    signal,
  );
export const selectionPage = (
  selection: string,
  offset = 0,
  signal?: AbortSignal,
  limit = 25,
) =>
  accountQueryRequest<AccountPage>(
    `/account-selections/${selection}/items?limit=${limit}&offset=${offset}`,
    undefined,
    signal,
  );
export const adjustAccountSelection = (
  selection: string,
  add: string[],
  remove: string[],
) =>
  accountQueryRequest<FrozenSelection>(
    `/account-selections/${selection}/adjust`,
    { add, remove },
  );
export class AccountQueryHttpError extends Error {
  constructor(message: string, public readonly status: number) { super(message); }
}

export const submitAccountJob = async (
  selection_id: string,
  action: BulkAction,
  preview_revision: string,
  key: string,
) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);
  try {
    return await accountQueryRequest<BulkJob>(
      "/account-bulk-jobs",
      { selection_id, action, preview_revision },
      controller.signal,
      key,
    );
  } catch (error) {
    if (error instanceof AccountQueryHttpError && error.status < 500) throw error;
    throw new Error(
      "Could not confirm whether changes were accepted. Retry uses the same request. You can also check Notifications.",
    );
  } finally {
    clearTimeout(timer);
  }
};
export const accountJob = (id: string, signal?: AbortSignal) =>
  accountQueryRequest<BulkJob>(`/account-bulk-jobs/${id}`, undefined, signal);

export const accountJobOutcomes = (
  id: string,
  offset = 0,
  signal?: AbortSignal,
  view: "all" | "exceptions" = "all",
) =>
  accountQueryRequest<OutcomePage>(
    `/account-bulk-jobs/${id}/items?limit=25&offset=${offset}&view=${view}`,
    undefined,
    signal,
  );
export async function exportQuery(
  q: AccountQuery,
  selection?: string,
): Promise<void> {
  const response = await fetch(
    `${import.meta.env.VITE_API_BASE_URL}/accounts/query/export`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getUserFromLS().access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ...q, selection_id: selection ?? null }),
    },
  );
  if (!response.ok) throw new Error("Could not export accounts. Please retry.");
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = "accounts.csv";
  link.click();
  URL.revokeObjectURL(url);
}
